import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface Miembro {
  id: string;
  nombre: string;
  nombreEnFlex?: string;
  correo: string;
  tipo: 'asociado' | 'asesor';
  inmobiliaria_doc_id?: string;
  propiedadesAsignadas: number;
}

interface Propiedad {
  id: string;
  list_number?: string;
  titulo?: string;
  tipo_propiedad?: string;
  precio_actual_mxn?: number;
  inmobiliaria_doc_id?: string;
  asesor_doc_ids?: string[];
  asociado_doc_id?: string;
}

interface PropiedadCSV {
  listNumber: string;
  agenteNombre: string;
  agenciaNombre: string;
}

interface PosibleMatch {
  agenteCSV: string;
  miembroFirestore: Miembro;
  similitud: number;
  palabrasCoinciden: string[];
  propiedadesEnCSV: number;
}

const DiagnosticoPropiedades: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [csvPropiedades, setCsvPropiedades] = useState<PropiedadCSV[]>([]);
  const [miembros, setMiembros] = useState<Miembro[]>([]);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);

  // Diagnósticos
  const [miembrosSinProps, setMiembrosSinProps] = useState<Miembro[]>([]);
  const [propsSinAsignar, setPropsSinAsignar] = useState<Propiedad[]>([]);
  const [agentesEnCSVNoEnFirestore, setAgentesEnCSVNoEnFirestore] = useState<string[]>([]);
  const [propsPorAgenteCSV, setPropsPorAgenteCSV] = useState<{ [key: string]: number }>({});
  const [posiblesMatches, setPosiblesMatches] = useState<PosibleMatch[]>([]);
  const [logs, setLogs] = useState<string[]>([]);

  // Para selección y edición de matches
  const [matchesSeleccionados, setMatchesSeleccionados] = useState<Set<number>>(new Set());
  const [nombresEditados, setNombresEditados] = useState<{ [idx: number]: string }>({});
  const [aplicandoCambios, setAplicandoCambios] = useState(false);

  // Para edición de miembros sin propiedades
  const [miembrosEditados, setMiembrosEditados] = useState<{ [id: string]: string }>({});
  const [miembrosSeleccionados, setMiembrosSeleccionados] = useState<Set<string>>(new Set());
  const [guardandoMiembros, setGuardandoMiembros] = useState(false);
  const [busquedaMiembro, setBusquedaMiembro] = useState('');

  const addLog = (msg: string) => {
    setLogs(prev => [...prev, msg]);
    console.log(msg);
  };

  const normalizeString = (str: string): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Obtener tokens (palabras) de un nombre
  const getTokens = (str: string): string[] => {
    const normalized = normalizeString(str);
    return normalized.split(' ').filter(t => t.length > 1); // Ignorar iniciales de 1 letra
  };

  // Calcular similitud entre dos nombres basado en tokens
  const calcularSimilitud = (nombre1: string, nombre2: string): { similitud: number; palabrasCoinciden: string[] } => {
    const tokens1 = getTokens(nombre1);
    const tokens2 = getTokens(nombre2);

    if (tokens1.length === 0 || tokens2.length === 0) {
      return { similitud: 0, palabrasCoinciden: [] };
    }

    const palabrasCoinciden: string[] = [];

    // Buscar tokens que coincidan (exacto o que uno contenga al otro)
    tokens1.forEach(t1 => {
      tokens2.forEach(t2 => {
        if (t1 === t2 || t1.includes(t2) || t2.includes(t1)) {
          if (!palabrasCoinciden.includes(t1) && !palabrasCoinciden.includes(t2)) {
            palabrasCoinciden.push(t1.length > t2.length ? t1 : t2);
          }
        }
      });
    });

    // Calcular similitud basada en proporción de coincidencias
    const totalTokens = Math.max(tokens1.length, tokens2.length);
    const similitud = (palabrasCoinciden.length / totalTokens) * 100;

    return { similitud, palabrasCoinciden };
  };

  // Buscar posibles matches para un agente del CSV
  const buscarPosiblesMatches = (agenteCSV: string, miembros: Miembro[], umbralSimilitud: number = 40): PosibleMatch[] => {
    const matches: PosibleMatch[] = [];

    miembros.forEach(miembro => {
      // Comparar con nombre
      const simNombre = calcularSimilitud(agenteCSV, miembro.nombre);
      // Comparar con nombreEnFlex
      const simFlex = miembro.nombreEnFlex ? calcularSimilitud(agenteCSV, miembro.nombreEnFlex) : { similitud: 0, palabrasCoinciden: [] };

      // Tomar la mejor similitud
      const mejor = simNombre.similitud > simFlex.similitud ? simNombre : simFlex;

      if (mejor.similitud >= umbralSimilitud) {
        matches.push({
          agenteCSV,
          miembroFirestore: miembro,
          similitud: mejor.similitud,
          palabrasCoinciden: mejor.palabrasCoinciden,
          propiedadesEnCSV: 0 // Se llena después
        });
      }
    });

    return matches.sort((a, b) => b.similitud - a.similitud);
  };

  // Manejar selección de match
  const toggleSeleccion = (idx: number) => {
    const nuevo = new Set(matchesSeleccionados);
    if (nuevo.has(idx)) {
      nuevo.delete(idx);
    } else {
      nuevo.add(idx);
      // Auto-rellenar el nombre si no está editado
      if (!nombresEditados[idx]) {
        setNombresEditados(prev => ({
          ...prev,
          [idx]: posiblesMatches[idx].agenteCSV
        }));
      }
    }
    setMatchesSeleccionados(nuevo);
  };

  // Seleccionar/deseleccionar todos
  const toggleTodos = () => {
    if (matchesSeleccionados.size === posiblesMatches.length) {
      setMatchesSeleccionados(new Set());
    } else {
      const todos = new Set<number>();
      posiblesMatches.forEach((_, idx) => {
        todos.add(idx);
        if (!nombresEditados[idx]) {
          setNombresEditados(prev => ({
            ...prev,
            [idx]: posiblesMatches[idx].agenteCSV
          }));
        }
      });
      setMatchesSeleccionados(todos);
    }
  };

  // Editar nombre en flex
  const editarNombre = (idx: number, valor: string) => {
    setNombresEditados(prev => ({
      ...prev,
      [idx]: valor
    }));
  };

  // Aplicar cambios seleccionados a Firestore
  const aplicarCambios = async () => {
    if (matchesSeleccionados.size === 0) {
      alert('Selecciona al menos un match para aplicar');
      return;
    }

    const confirmacion = window.confirm(
      `¿Aplicar cambios a ${matchesSeleccionados.size} miembros?\n\nEsto actualizará el campo "nombreEnFlex" para que coincida con el nombre en el CSV.`
    );

    if (!confirmacion) return;

    setAplicandoCambios(true);
    let exitosos = 0;
    let errores = 0;

    for (const idx of Array.from(matchesSeleccionados)) {
      const match = posiblesMatches[idx];
      const nuevoNombre = nombresEditados[idx] || match.agenteCSV;

      try {
        const coleccion = match.miembroFirestore.tipo === 'asociado' ? 'asociado' : 'asesor';
        const docRef = doc(db, coleccion, match.miembroFirestore.id);

        await updateDoc(docRef, {
          nombreEnFlex: nuevoNombre
        });

        exitosos++;
        addLog(`✅ Actualizado: ${match.miembroFirestore.nombre} → nombreEnFlex: "${nuevoNombre}"`);
      } catch (error: any) {
        errores++;
        addLog(`❌ Error actualizando ${match.miembroFirestore.nombre}: ${error.message}`);
      }
    }

    setAplicandoCambios(false);
    addLog('');
    addLog(`📊 RESULTADO: ${exitosos} exitosos, ${errores} errores`);

    if (exitosos > 0) {
      alert(`✅ ${exitosos} miembros actualizados correctamente.\n\nAhora puedes ejecutar la asignación masiva de propiedades.`);
      // Limpiar selección
      setMatchesSeleccionados(new Set());
      setNombresEditados({});
    }
  };

  // Buscar nombres similares en el CSV para un miembro
  const buscarSimilaresEnCSV = (miembro: Miembro): { nombre: string; similitud: number; propiedades: number }[] => {
    if (csvPropiedades.length === 0) return [];

    const resultados: { nombre: string; similitud: number; propiedades: number }[] = [];
    const agentesUnicos = new Set<string>();

    csvPropiedades.forEach(p => agentesUnicos.add(p.agenteNombre));

    agentesUnicos.forEach(agente => {
      const sim = calcularSimilitud(miembro.nombreEnFlex || miembro.nombre, agente);
      if (sim.similitud >= 30) {
        const props = csvPropiedades.filter(p => p.agenteNombre === agente).length;
        resultados.push({
          nombre: agente,
          similitud: sim.similitud,
          propiedades: props
        });
      }
    });

    return resultados.sort((a, b) => b.similitud - a.similitud).slice(0, 5);
  };

  // Toggle selección de miembro
  const toggleMiembroSeleccion = (id: string) => {
    const nuevo = new Set(miembrosSeleccionados);
    if (nuevo.has(id)) {
      nuevo.delete(id);
    } else {
      nuevo.add(id);
    }
    setMiembrosSeleccionados(nuevo);
  };

  // Editar nombreEnFlex de miembro
  const editarMiembroNombreFlex = (id: string, valor: string) => {
    setMiembrosEditados(prev => ({ ...prev, [id]: valor }));
    // Auto-seleccionar al editar
    if (!miembrosSeleccionados.has(id)) {
      setMiembrosSeleccionados(prev => new Set(prev).add(id));
    }
  };

  // Seleccionar nombre del CSV para un miembro
  const seleccionarNombreCSV = (miembroId: string, nombreCSV: string) => {
    setMiembrosEditados(prev => ({ ...prev, [miembroId]: nombreCSV }));
    if (!miembrosSeleccionados.has(miembroId)) {
      setMiembrosSeleccionados(prev => new Set(prev).add(miembroId));
    }
  };

  // Guardar cambios de miembros
  const guardarCambiosMiembros = async () => {
    if (miembrosSeleccionados.size === 0) {
      alert('Selecciona al menos un miembro para guardar');
      return;
    }

    const miembrosConCambios = Array.from(miembrosSeleccionados).filter(id => miembrosEditados[id]);
    if (miembrosConCambios.length === 0) {
      alert('No hay cambios para guardar. Edita el "Nombre en Flex" primero.');
      return;
    }

    const confirmacion = window.confirm(
      `¿Guardar cambios en ${miembrosConCambios.length} miembros?\n\nEsto actualizará el campo "nombreEnFlex".`
    );

    if (!confirmacion) return;

    setGuardandoMiembros(true);
    let exitosos = 0;
    let errores = 0;

    for (const id of miembrosConCambios) {
      const miembro = miembrosSinProps.find(m => m.id === id);
      if (!miembro) continue;

      const nuevoNombre = miembrosEditados[id];

      try {
        const coleccion = miembro.tipo === 'asociado' ? 'asociado' : 'asesor';
        const docRef = doc(db, coleccion, miembro.id);

        await updateDoc(docRef, {
          nombreEnFlex: nuevoNombre
        });

        exitosos++;
        addLog(`✅ ${miembro.nombre} → nombreEnFlex: "${nuevoNombre}"`);
      } catch (error: any) {
        errores++;
        addLog(`❌ Error en ${miembro.nombre}: ${error.message}`);
      }
    }

    setGuardandoMiembros(false);
    addLog('');
    addLog(`📊 Miembros actualizados: ${exitosos} exitosos, ${errores} errores`);

    if (exitosos > 0) {
      alert(`✅ ${exitosos} miembros actualizados.\n\nVuelve a ejecutar el diagnóstico para verificar los matches.`);
      setMiembrosSeleccionados(new Set());
      setMiembrosEditados({});
    }
  };

  // Cargar CSV de propiedades
  const handlePropiedadesCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data: PropiedadCSV[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const listNumberIdx = headers.indexOf('listNumber');
        const agenteNombreIdx = headers.indexOf('agenteNombre');
        const agenciaNombreIdx = headers.indexOf('agenciaNombre');

        if (values[agenteNombreIdx]) {
          data.push({
            listNumber: values[listNumberIdx] || '',
            agenteNombre: values[agenteNombreIdx] || '',
            agenciaNombre: values[agenciaNombreIdx] || ''
          });
        }
      }

      setCsvPropiedades(data);
      addLog(`📄 CSV cargado: ${data.length} propiedades con agente`);
    };
    reader.readAsText(file);
  };

  // Ejecutar diagnóstico completo
  const ejecutarDiagnostico = async () => {
    setLoading(true);
    setLogs([]);

    try {
      addLog('🔍 Iniciando diagnóstico completo...');

      // Cargar miembros de Firestore
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asesoresSnap = await getDocs(collection(db, 'asesor'));

      const asociadosData: Miembro[] = asociadosSnap.docs.map(d => ({
        id: d.id,
        nombre: d.data().nombre || '',
        nombreEnFlex: d.data().nombreEnFlex || d.data().nombre || '',
        correo: d.data().correo || '',
        tipo: 'asociado' as const,
        inmobiliaria_doc_id: d.data().inmobiliaria_doc_id,
        propiedadesAsignadas: 0
      }));

      const asesoresData: Miembro[] = asesoresSnap.docs.map(d => ({
        id: d.id,
        nombre: d.data().nombre || '',
        nombreEnFlex: d.data().nombreEnFlex || d.data().nombre || '',
        correo: d.data().correo || '',
        tipo: 'asesor' as const,
        inmobiliaria_doc_id: d.data().inmobiliaria_doc_ids?.[0],
        propiedadesAsignadas: 0
      }));

      const todosMiembros = [...asociadosData, ...asesoresData];
      addLog(`✅ ${todosMiembros.length} miembros en Firestore`);

      // Cargar propiedades de Firestore
      const propSnap = await getDocs(collection(db, 'propiedades'));
      const propsData: Propiedad[] = propSnap.docs.map(d => ({
        id: d.id,
        list_number: d.data().list_number || '',
        titulo: d.data().titulo || '',
        tipo_propiedad: d.data().tipo_propiedad || '',
        precio_actual_mxn: d.data().precio_actual_mxn || 0,
        inmobiliaria_doc_id: d.data().inmobiliaria_doc_id,
        asesor_doc_ids: d.data().asesor_doc_ids || [],
        asociado_doc_id: d.data().asociado_doc_id
      }));
      addLog(`✅ ${propsData.length} propiedades en Firestore`);

      // Contar propiedades por miembro
      const conteoProps: { [key: string]: number } = {};
      propsData.forEach(p => {
        if (p.asesor_doc_ids) {
          p.asesor_doc_ids.forEach(id => {
            conteoProps[id] = (conteoProps[id] || 0) + 1;
          });
        }
        if (p.asociado_doc_id) {
          conteoProps[p.asociado_doc_id] = (conteoProps[p.asociado_doc_id] || 0) + 1;
        }
      });

      todosMiembros.forEach(m => {
        m.propiedadesAsignadas = conteoProps[m.id] || 0;
      });

      setMiembros(todosMiembros);
      setPropiedades(propsData);

      // Miembros sin propiedades
      const sinProps = todosMiembros.filter(m => m.propiedadesAsignadas === 0);
      setMiembrosSinProps(sinProps);
      addLog(`📊 ${sinProps.length} miembros SIN propiedades asignadas`);

      // Propiedades sin asignar
      const sinAsignar = propsData.filter(p =>
        (!p.asesor_doc_ids || p.asesor_doc_ids.length === 0) && !p.asociado_doc_id
      );
      setPropsSinAsignar(sinAsignar);
      addLog(`📊 ${sinAsignar.length} propiedades SIN miembro asignado`);

      // Análisis del CSV
      if (csvPropiedades.length > 0) {
        addLog('');
        addLog('📋 ANÁLISIS DEL CSV DE PROPIEDADES:');

        // Contar propiedades por agente en CSV
        const porAgente: { [key: string]: number } = {};
        csvPropiedades.forEach(p => {
          const nombre = p.agenteNombre;
          porAgente[nombre] = (porAgente[nombre] || 0) + 1;
        });
        setPropsPorAgenteCSV(porAgente);

        const agentesUnicos = Object.keys(porAgente);
        addLog(`   ${agentesUnicos.length} agentes únicos en CSV`);

        // Crear set de nombres normalizados de miembros en Firestore
        const nombresFirestore = new Set<string>();
        todosMiembros.forEach(m => {
          nombresFirestore.add(normalizeString(m.nombre));
          if (m.nombreEnFlex) {
            nombresFirestore.add(normalizeString(m.nombreEnFlex));
          }
        });

        // Buscar agentes del CSV que NO están en Firestore
        const noEncontrados: string[] = [];
        agentesUnicos.forEach(agente => {
          const normalizado = normalizeString(agente);
          if (!nombresFirestore.has(normalizado)) {
            noEncontrados.push(agente);
          }
        });

        setAgentesEnCSVNoEnFirestore(noEncontrados);
        addLog(`   ${noEncontrados.length} agentes del CSV NO encontrados (match exacto) en Firestore`);

        // Propiedades de esos agentes no encontrados
        let propsSinAgente = 0;
        noEncontrados.forEach(agente => {
          propsSinAgente += porAgente[agente] || 0;
        });
        addLog(`   ${propsSinAgente} propiedades pertenecen a agentes sin match exacto`);

        // NUEVO: Buscar posibles matches parciales para los no encontrados
        addLog('');
        addLog('🔎 BÚSQUEDA DE POSIBLES MATCHES (similitud por palabras):');

        const todosLosPosiblesMatches: PosibleMatch[] = [];
        let conPosibleMatch = 0;
        let sinNingunMatch = 0;

        noEncontrados.forEach(agente => {
          const matches = buscarPosiblesMatches(agente, todosMiembros, 40);

          if (matches.length > 0) {
            conPosibleMatch++;
            const mejor = matches[0];
            mejor.propiedadesEnCSV = porAgente[agente] || 0;
            todosLosPosiblesMatches.push(mejor);

            addLog(`   🔗 "${agente}" → posible match: "${mejor.miembroFirestore.nombre}" (${mejor.similitud.toFixed(0)}% - palabras: ${mejor.palabrasCoinciden.join(', ')})`);
          } else {
            sinNingunMatch++;
          }
        });

        setPosiblesMatches(todosLosPosiblesMatches);
        addLog('');
        addLog(`📊 RESUMEN DE POSIBLES MATCHES:`);
        addLog(`   ✅ ${conPosibleMatch} agentes con posible match encontrado`);
        addLog(`   ❌ ${sinNingunMatch} agentes sin ningún match posible (probablemente no registrados)`);

        // Buscar si los miembros sin propiedades tienen propiedades en el CSV
        addLog('');
        addLog('🔍 BÚSQUEDA DE PROPIEDADES PARA MIEMBROS SIN ASIGNACIÓN:');

        let encontradosEnCSV = 0;
        sinProps.forEach(miembro => {
          const nombreNorm = normalizeString(miembro.nombreEnFlex || miembro.nombre);

          // Buscar en CSV (match exacto)
          const propsEnCSV = csvPropiedades.filter(p =>
            normalizeString(p.agenteNombre) === nombreNorm
          );

          if (propsEnCSV.length > 0) {
            encontradosEnCSV++;
            addLog(`   ✅ ${miembro.nombre} tiene ${propsEnCSV.length} propiedades en CSV (match exacto)`);
          } else {
            // Buscar con similitud
            const propsPorSimilitud = csvPropiedades.filter(p => {
              const sim = calcularSimilitud(p.agenteNombre, miembro.nombreEnFlex || miembro.nombre);
              return sim.similitud >= 50;
            });

            if (propsPorSimilitud.length > 0) {
              encontradosEnCSV++;
              const ejemploAgente = propsPorSimilitud[0].agenteNombre;
              addLog(`   🔗 ${miembro.nombre} posible match con "${ejemploAgente}" (${propsPorSimilitud.length} props)`);
            }
          }
        });

        if (encontradosEnCSV === 0) {
          addLog(`   ⚠️ Ningún miembro sin propiedades tiene propiedades en el CSV`);
          addLog(`   Esto significa que estos miembros simplemente no tienen propiedades en Flex`);
        }
      }

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Diagnóstico de Propiedades' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-info text-white">
            <h4 className="mb-0">
              <i className="fas fa-stethoscope me-2"></i>
              Diagnóstico de Asignación de Propiedades
            </h4>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <p className="mb-0">
                Esta herramienta analiza por qué hay miembros sin propiedades y propiedades sin asignar,
                y determina si el problema es de match de nombres o si simplemente no hay propiedades para asignar.
              </p>
            </div>

            {/* Carga de CSV */}
            <div className="card border-primary mb-4">
              <div className="card-header bg-primary text-white">
                <i className="fas fa-file-csv me-2"></i>Cargar CSV de Propiedades
              </div>
              <div className="card-body">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handlePropiedadesCSV}
                  className="form-control mb-2"
                />
                <small className="text-muted">propiedades-completas.csv</small>
                {csvPropiedades.length > 0 && (
                  <div className="mt-2 text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    {csvPropiedades.length} propiedades cargadas
                  </div>
                )}
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg mb-4"
              onClick={ejecutarDiagnostico}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Analizando...
                </>
              ) : (
                <>
                  <i className="fas fa-search me-2"></i>
                  Ejecutar Diagnóstico
                </>
              )}
            </button>

            {/* Resultados */}
            <div className="row">
              {/* Miembros sin propiedades - MEJORADO */}
              {miembrosSinProps.length > 0 && (
                <div className="col-12 mb-4">
                  <div className="card border-warning">
                    <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                      <div>
                        <i className="fas fa-users me-2"></i>
                        Miembros SIN propiedades ({miembrosSinProps.length})
                        {miembrosSeleccionados.size > 0 && (
                          <span className="badge bg-dark ms-2">{miembrosSeleccionados.size} seleccionados</span>
                        )}
                      </div>
                      <div>
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={guardarCambiosMiembros}
                          disabled={guardandoMiembros || miembrosSeleccionados.size === 0}
                        >
                          {guardandoMiembros ? (
                            <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</>
                          ) : (
                            <><i className="fas fa-save me-1"></i>Guardar Cambios ({miembrosSeleccionados.size})</>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-info py-2">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Instrucciones:</strong> Busca el nombre correcto en el CSV y haz clic en él para asignarlo como "Nombre en Flex".
                        Luego guarda los cambios y vuelve a ejecutar el diagnóstico.
                      </div>

                      {/* Filtro de búsqueda */}
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="🔍 Filtrar miembros por nombre..."
                          value={busquedaMiembro}
                          onChange={(e) => setBusquedaMiembro(e.target.value)}
                        />
                      </div>

                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <table className="table table-sm table-hover">
                          <thead className="sticky-top bg-white">
                            <tr>
                              <th style={{ width: '30px' }}></th>
                              <th>Miembro</th>
                              <th>Nombre en Flex (editable)</th>
                              <th>Posibles matches en CSV (clic para seleccionar)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {miembrosSinProps
                              .filter(m =>
                                busquedaMiembro === '' ||
                                m.nombre.toLowerCase().includes(busquedaMiembro.toLowerCase()) ||
                                (m.nombreEnFlex || '').toLowerCase().includes(busquedaMiembro.toLowerCase())
                              )
                              .slice(0, 100)
                              .map((m) => {
                                const similares = buscarSimilaresEnCSV(m);
                                return (
                                  <tr key={m.id} className={miembrosSeleccionados.has(m.id) ? 'table-warning' : ''}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={miembrosSeleccionados.has(m.id)}
                                        onChange={() => toggleMiembroSeleccion(m.id)}
                                      />
                                    </td>
                                    <td>
                                      <strong>{m.nombre}</strong>
                                      <br />
                                      <small className="text-muted">
                                        <span className={`badge ${m.tipo === 'asociado' ? 'bg-success' : 'bg-info'} me-1`}>
                                          {m.tipo}
                                        </span>
                                        Actual: {m.nombreEnFlex || '(vacío)'}
                                      </small>
                                    </td>
                                    <td style={{ minWidth: '200px' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={miembrosEditados[m.id] ?? m.nombreEnFlex ?? ''}
                                        onChange={(e) => editarMiembroNombreFlex(m.id, e.target.value)}
                                        placeholder="Escribe el nombre en Flex..."
                                      />
                                    </td>
                                    <td>
                                      {csvPropiedades.length === 0 ? (
                                        <small className="text-muted">Carga el CSV primero</small>
                                      ) : similares.length === 0 ? (
                                        <small className="text-muted">Sin coincidencias</small>
                                      ) : (
                                        <div className="d-flex flex-wrap gap-1">
                                          {similares.map((s, i) => (
                                            <button
                                              key={i}
                                              className={`btn btn-sm ${
                                                miembrosEditados[m.id] === s.nombre
                                                  ? 'btn-success'
                                                  : s.similitud >= 60
                                                    ? 'btn-outline-success'
                                                    : 'btn-outline-secondary'
                                              }`}
                                              onClick={() => seleccionarNombreCSV(m.id, s.nombre)}
                                              title={`${s.propiedades} propiedades - ${s.similitud.toFixed(0)}% similitud`}
                                            >
                                              {s.nombre}
                                              <span className="badge bg-light text-dark ms-1">{s.propiedades}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                      {miembrosSinProps.length > 100 && (
                        <p className="text-muted mt-2">Mostrando 100 de {miembrosSinProps.length}. Usa el filtro para buscar.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Agentes del CSV no en Firestore */}
              {agentesEnCSVNoEnFirestore.length > 0 && (
                <div className="col-md-6 mb-4">
                  <div className="card border-danger h-100">
                    <div className="card-header bg-danger text-white">
                      <i className="fas fa-user-times me-2"></i>
                      Agentes sin match exacto ({agentesEnCSVNoEnFirestore.length})
                    </div>
                    <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <p className="text-muted small">
                        Estos agentes del CSV no tienen match exacto. Revisa la tabla de posibles matches abajo.
                      </p>
                      <table className="table table-sm table-striped">
                        <thead className="sticky-top bg-white">
                          <tr>
                            <th>Nombre del Agente</th>
                            <th># Props</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentesEnCSVNoEnFirestore
                            .sort((a, b) => (propsPorAgenteCSV[b] || 0) - (propsPorAgenteCSV[a] || 0))
                            .slice(0, 50)
                            .map((agente, idx) => (
                              <tr key={idx}>
                                <td>{agente}</td>
                                <td>
                                  <span className="badge bg-primary">
                                    {propsPorAgenteCSV[agente] || 0}
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {agentesEnCSVNoEnFirestore.length > 50 && (
                        <p className="text-muted">...y {agentesEnCSVNoEnFirestore.length - 50} más</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* NUEVA SECCIÓN: Posibles Matches */}
            {posiblesMatches.length > 0 && (
              <div className="card border-success mb-4">
                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-link me-2"></i>
                    Posibles Matches Encontrados ({posiblesMatches.length})
                    {matchesSeleccionados.size > 0 && (
                      <span className="badge bg-light text-success ms-2">
                        {matchesSeleccionados.size} seleccionados
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      className="btn btn-light btn-sm me-2"
                      onClick={toggleTodos}
                    >
                      <i className={`fas fa-${matchesSeleccionados.size === posiblesMatches.length ? 'square' : 'check-square'} me-1`}></i>
                      {matchesSeleccionados.size === posiblesMatches.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={aplicarCambios}
                      disabled={aplicandoCambios || matchesSeleccionados.size === 0}
                    >
                      {aplicandoCambios ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Aplicando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-1"></i>
                          Aplicar Seleccionados ({matchesSeleccionados.size})
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Instrucciones:</strong>
                    <ol className="mb-0 mt-2">
                      <li>Revisa cada match sugerido</li>
                      <li>Marca con ✓ los que son correctos</li>
                      <li>Edita el "Nombre en Flex" si es necesario</li>
                      <li>Haz clic en "Aplicar Seleccionados"</li>
                      <li>Luego ejecuta la asignación masiva de propiedades</li>
                    </ol>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table className="table table-sm table-hover">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={matchesSeleccionados.size === posiblesMatches.length}
                              onChange={toggleTodos}
                            />
                          </th>
                          <th>Agente en CSV</th>
                          <th style={{ width: '30px' }}></th>
                          <th>Miembro en Firestore</th>
                          <th>Nuevo Nombre en Flex</th>
                          <th style={{ width: '70px' }}>Similitud</th>
                          <th style={{ width: '60px' }}># Props</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {posiblesMatches
                          .map((match, idx) => ({ match, idx }))
                          .sort((a, b) => b.match.propiedadesEnCSV - a.match.propiedadesEnCSV)
                          .map(({ match, idx }) => (
                            <tr
                              key={idx}
                              className={matchesSeleccionados.has(idx) ? 'table-success' : ''}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={matchesSeleccionados.has(idx)}
                                  onChange={() => toggleSeleccion(idx)}
                                />
                              </td>
                              <td>
                                <strong className="text-primary">{match.agenteCSV}</strong>
                                <br />
                                <small className="text-muted">
                                  Palabras: {match.palabrasCoinciden.join(', ')}
                                </small>
                              </td>
                              <td><i className="fas fa-arrow-right text-muted"></i></td>
                              <td>
                                <strong>{match.miembroFirestore.nombre}</strong>
                                <br />
                                <small className="text-muted">
                                  {match.miembroFirestore.tipo} |
                                  Actual: {match.miembroFirestore.nombreEnFlex || '(vacío)'}
                                </small>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={nombresEditados[idx] ?? match.agenteCSV}
                                  onChange={(e) => editarNombre(idx, e.target.value)}
                                  placeholder={match.agenteCSV}
                                  disabled={!matchesSeleccionados.has(idx)}
                                />
                              </td>
                              <td>
                                <span className={`badge ${
                                  match.similitud >= 70 ? 'bg-success' :
                                  match.similitud >= 50 ? 'bg-warning text-dark' :
                                  'bg-secondary'
                                }`}>
                                  {match.similitud.toFixed(0)}%
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-info">{match.propiedadesEnCSV}</span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => {
                                    const tipo = match.miembroFirestore.tipo;
                                    const id = match.miembroFirestore.id;
                                    window.open(`/${tipo === 'asociado' ? 'asociado-page' : 'agent-page'}/${id}`, '_blank');
                                  }}
                                  title="Ver perfil"
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Logs */}
            {logs.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <i className="fas fa-terminal me-2"></i>
                  Resultado del Diagnóstico
                </div>
                <div className="card-body bg-dark text-light" style={{ maxHeight: '400px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {logs.map((log, idx) => (
                    <div key={idx} className={
                      log.includes('❌') ? 'text-danger' :
                      log.includes('✅') ? 'text-success' :
                      log.includes('⚠️') ? 'text-warning' :
                      log.includes('📊') ? 'text-info fw-bold' :
                      log.includes('🔍') ? 'text-primary fw-bold mt-2' :
                      log.includes('📋') ? 'text-warning fw-bold mt-2' :
                      'text-light'
                    }>
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DiagnosticoPropiedades;
